import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Truck, Upload, CheckCircle } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DispatchRow({ o, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [d, setD] = useState({
    courier: o.dispatch?.courier || "",
    tracking_no: o.dispatch?.tracking_no || "",
    vehicle: o.dispatch?.vehicle || "",
    driver: o.dispatch?.driver || "",
    note: "",
  });
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    try { await api.patch(`/orders/${o.id}/dispatch`, d); onSaved(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const markShipped = async () => {
    try {
      await save();
      await api.patch(`/orders/${o.id}/status`, { status: "out_for_delivery", note: `Dispatched via ${d.courier || d.vehicle || "delivery"}` });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const uploadPod = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${o.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/orders/${o.id}/status`, { status: "delivered", note: "POD uploaded" });
      onSaved();
    } catch (er) { alert(formatApiError(er)); } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="border-b border-[hsl(var(--border))]" data-testid={`dispatch-row-${o.id}`}>
      <div className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-[hsl(var(--muted))] cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <Link to={`/orders/${o.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs font-semibold hover:text-[#FF6B00] min-w-[110px]">{o.id}</Link>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{o.customer_name}</div>
          <div className="text-xs text-muted-foreground truncate">{o.product_name || o.product_category}</div>
        </div>
        <div className="hidden md:block text-xs text-muted-foreground">{o.customer_phone}</div>
        <StatusBadge status={o.status} />
        {o.pod_id && <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={12} /> POD</span>}
      </div>
      {expanded && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs bg-[hsl(var(--muted))]" data-testid={`dispatch-form-${o.id}`}>
          <input placeholder="Courier / partner" value={d.courier} onChange={(e) => setD({ ...d, courier: e.target.value })}
            className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5" data-testid={`courier-${o.id}`} />
          <input placeholder="Tracking #" value={d.tracking_no} onChange={(e) => setD({ ...d, tracking_no: e.target.value })}
            className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5" data-testid={`tracking-${o.id}`} />
          <input placeholder="Vehicle no." value={d.vehicle} onChange={(e) => setD({ ...d, vehicle: e.target.value })}
            className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5" data-testid={`vehicle-${o.id}`} />
          <input placeholder="Driver name" value={d.driver} onChange={(e) => setD({ ...d, driver: e.target.value })}
            className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5" data-testid={`driver-${o.id}`} />
          {err && <div className="md:col-span-4 text-red-600">{err}</div>}
          <div className="md:col-span-4 flex flex-wrap gap-2 pt-1">
            <button onClick={save} className="btn-swiss btn-outline text-xs" data-testid={`save-dispatch-${o.id}`}>Save</button>
            {o.status === "ready" && (
              <button onClick={markShipped} className="btn-swiss btn-accent text-xs" data-testid={`ship-${o.id}`}>
                <Truck size={12} /> Mark Out for Delivery
              </button>
            )}
            {(o.status === "out_for_delivery" || o.status === "ready") && (
              <label className="btn-swiss btn-outline text-xs cursor-pointer" data-testid={`upload-pod-label-${o.id}`}
                style={{ borderColor: "#25D366", color: "#25D366" }}>
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload Proof of Delivery"}
                <input type="file" accept="image/*,application/pdf" onChange={uploadPod} className="hidden" data-testid={`upload-pod-input-${o.id}`} disabled={uploading} />
              </label>
            )}
            {o.pod_id && (
              <a href={`${API}/orders/${o.id}/pod`} target="_blank" rel="noreferrer" className="btn-swiss btn-outline text-xs" data-testid={`view-pod-${o.id}`}>
                View POD
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dispatch() {
  usePageTitle("Dispatch");
  const [rows, setRows] = useState([]);
  const load = () => api.get("/dispatch/queue").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="dispatch-root">
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Delivery</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Dispatch</h1>
        <p className="text-sm text-muted-foreground mt-2">Ready-to-ship jobs. Add courier / vehicle details and upload Proof of Delivery when handed over.</p>
      </div>
      <div className="card-swiss overflow-hidden">
        {rows.map((o) => <DispatchRow key={o.id} o={o} onSaved={load} />)}
        {rows.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No orders in dispatch queue.</div>}
      </div>
    </div>
  );
}
